import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  LayoutGrid, ArrowLeft, Clock, CheckCircle2, Truck, PackageCheck,
  Factory, ShieldCheck, CircleDot, MapPin, Calendar, DollarSign, RefreshCw
} from 'lucide-react';
import AppHeader from '@/components/AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STEPS = [
  { key: 'pending', label: 'Order Placed', icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2, color: 'text-blue-600 bg-blue-50 border-blue-200' },
  { key: 'in_production', label: 'In Production', icon: Factory, color: 'text-orange-600 bg-orange-50 border-orange-200' },
  { key: 'quality_check', label: 'Quality Check', icon: ShieldCheck, color: 'text-purple-600 bg-purple-50 border-purple-200' },
  { key: 'ready_for_delivery', label: 'Ready for Delivery', icon: PackageCheck, color: 'text-teal-600 bg-teal-50 border-teal-200' },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2, color: 'text-green-600 bg-green-50 border-green-200' },
  { key: 'completed', label: 'Completed', icon: CheckCircle2, color: 'text-green-700 bg-green-100 border-green-300' }
];

export default function OrderTracking() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/orders/${orderId}/track`, {
        withCredentials: true
      });
      setOrder(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  const getCurrentStepIndex = () => {
    if (!order) return -1;
    return STATUS_STEPS.findIndex(s => s.key === order.status);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-slate-500 text-lg">Loading order details...</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Card className="border-slate-200 rounded-sm shadow-none max-w-md w-full">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500 mb-4">Order not found</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-[#0052FF] hover:bg-[#0043D1] rounded-sm">
              Back to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentStep = getCurrentStepIndex();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AppHeader title="Hi Tech Industries" backTo="/dashboard" />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Title row */}
        <div className="flex items-start justify-between mb-6 sm:mb-8 gap-2">
          <div>
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-1">Order Tracking</h2>
            <p className="text-xs sm:text-base text-slate-500">Order #{order.id.slice(0, 8)}</p>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] sm:text-xs text-slate-400 shrink-0">
            <RefreshCw size={12} className="animate-spin-slow" />
            {lastUpdated && <span className="hidden sm:inline">Updated {lastUpdated.toLocaleTimeString()}</span>}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">
          {/* Timeline - Main Column */}
          <div className="lg:col-span-8 space-y-4 sm:space-y-6">
            <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="status-timeline">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-lg sm:text-xl font-medium">Status Timeline</CardTitle>
                <CardDescription className="text-xs sm:text-sm text-slate-500">Auto-refreshes every 10 seconds</CardDescription>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="relative">
                  {STATUS_STEPS.map((step, index) => {
                    const isCompleted = index <= currentStep;
                    const isCurrent = index === currentStep;
                    const StepIcon = step.icon;
                    const historyEntry = order.status_history?.find(h => h.status === step.key);

                    return (
                      <div key={step.key} className="relative flex items-start pb-8 last:pb-0" data-testid={`status-step-${step.key}`}>
                        {/* Vertical line */}
                        {index < STATUS_STEPS.length - 1 && (
                          <div className={`absolute left-5 top-10 w-0.5 h-full -ml-px ${
                            index < currentStep ? 'bg-[#0052FF]' : 'bg-slate-200'
                          }`} />
                        )}

                        {/* Icon circle */}
                        <div className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 shrink-0 transition-all duration-300 ${
                          isCurrent
                            ? 'border-[#0052FF] bg-[#0052FF] text-white scale-110 ring-4 ring-[#0052FF]/20'
                            : isCompleted
                              ? 'border-[#0052FF] bg-[#0052FF] text-white'
                              : 'border-slate-200 bg-white text-slate-400'
                        }`}>
                          <StepIcon size={18} />
                        </div>

                        {/* Content */}
                        <div className="ml-4 flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <h4 className={`font-medium ${
                              isCompleted ? 'text-slate-900' : 'text-slate-400'
                            }`}>
                              {step.label}
                            </h4>
                            {isCurrent && (
                              <Badge className="bg-[#0052FF] text-white rounded-sm text-xs animate-pulse" data-testid="current-status-badge">
                                Current
                              </Badge>
                            )}
                          </div>
                          {historyEntry && (
                            <div className="mt-1">
                              <p className="text-sm text-slate-500">{historyEntry.note}</p>
                              <p className="text-xs text-slate-400 mt-0.5 font-mono">{formatDate(historyEntry.timestamp)}</p>
                            </div>
                          )}
                          {!historyEntry && !isCompleted && (
                            <p className="text-sm text-slate-300 mt-1">Pending</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Status History Log */}
            {order.status_history && order.status_history.length > 0 && (
              <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="status-history">
                <CardHeader className="p-6">
                  <CardTitle className="text-xl font-medium">Activity Log</CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <div className="space-y-3">
                    {[...order.status_history].reverse().map((entry, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-sm border border-slate-100" data-testid={`history-entry-${i}`}>
                        <CircleDot size={16} className="text-[#0052FF] mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-slate-900">{entry.note}</span>
                            <span className="text-xs font-mono text-slate-400">{formatDate(entry.timestamp)}</span>
                          </div>
                          <Badge className="mt-1 bg-slate-100 text-slate-600 rounded-sm text-xs">{entry.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right sidebar - Order Details */}
          <div className="lg:col-span-4 space-y-6">
            <Card className="border border-slate-200 rounded-sm shadow-none sticky top-24" data-testid="order-details">
              <CardHeader className="p-6">
                <CardTitle className="text-xl font-medium">Order Details</CardTitle>
              </CardHeader>
              <CardContent className="p-6 pt-0 space-y-5">
                {/* Product */}
                <div>
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Product</span>
                  <p className="text-slate-900 font-medium mt-1">{order.quote?.product?.name || 'N/A'}</p>
                  <p className="text-sm text-slate-500">{order.quote?.product?.style} | {order.quote?.product?.material}</p>
                </div>

                {/* Dimensions */}
                <div>
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Dimensions</span>
                  <p className="text-slate-900 font-mono font-medium mt-1">
                    {order.quote?.width} x {order.quote?.height} ft
                    <span className="text-slate-500 ml-2">({order.quote?.area?.toFixed(2)} sq ft)</span>
                  </p>
                </div>

                {/* Quantity */}
                <div>
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Quantity</span>
                  <p className="text-slate-900 font-mono font-medium mt-1">{order.quote?.quantity} unit(s)</p>
                </div>

                {/* Delivery Address */}
                <div className="flex gap-2">
                  <MapPin size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Delivery Address</span>
                    <p className="text-slate-900 mt-1">{order.delivery_address}</p>
                  </div>
                </div>

                {/* Delivery Date */}
                {order.delivery_date && (
                  <div className="flex gap-2">
                    <Calendar size={16} className="text-slate-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Expected Delivery</span>
                      <p className="text-slate-900 mt-1">{order.delivery_date}</p>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="border-t border-slate-200 pt-4">
                  <div className="flex items-center gap-2">
                    <DollarSign size={18} className="text-[#0052FF]" />
                    <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Total Amount</span>
                  </div>
                  <p className="text-3xl font-medium text-[#0052FF] font-mono mt-2" data-testid="order-total-price">
                    &#8377;{order.quote?.total_price?.toFixed(2)}
                  </p>
                </div>

                {/* Order Date */}
                <div className="border-t border-slate-200 pt-4">
                  <span className="text-xs font-medium uppercase tracking-[0.2em] text-slate-500">Order Placed</span>
                  <p className="text-slate-900 text-sm font-mono mt-1">{formatDate(order.created_at)}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
